<template>
  <transition name="fade">
    <div v-if="modelValue" class="alert-dialog-overlay" @click="handleOverlayClick">
      <div class="alert-dialog" @click.stop>
        <!-- Dialog Header -->
        <div v-if="title" class="alert-dialog__header">
          <h3 class="alert-dialog__title" :class="{ 'no-message': !message }">{{ title }}</h3>
        </div>

        <!-- Dialog Content -->
        <div class="alert-dialog__content">
          <p v-if="message" class="alert-dialog__message">{{ message }}</p>
          <slot></slot>
        </div>

        <!-- Dialog Actions -->
        <div class="alert-dialog__actions">
          <button
            v-if="showCancelButton"
            class="alert-dialog__button alert-dialog__button--cancel"
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
          <button
            v-if="showNeutralButton"
            class="alert-dialog__button alert-dialog__button--neutral"
            @click="handleNeutral"
          >
            {{ neutralText }}
          </button>
          <button
            class="alert-dialog__button alert-dialog__button--confirm"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  },
  confirmText: {
    type: String,
    default: '확인'
  },
  cancelText: {
    type: String,
    default: '취소'
  },
  neutralText: {
    type: String,
    default: '더보기'
  },
  closeOnOverlayClick: {
    type: Boolean,
    default: false
  },
  showCancelButton: {
    type: Boolean,
    default: true
  },
  showNeutralButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'neutral'])

const handleConfirm = () => {
  emit('confirm')
  emit('update:modelValue', false)
}

const handleCancel = () => {
  emit('cancel')
  emit('update:modelValue', false)
}

const handleNeutral = () => {
  emit('neutral')
  emit('update:modelValue', false)
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) {
    emit('update:modelValue', false)
  }
}
</script>

<style scoped>
.alert-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.alert-dialog {
  background-color: white;
  border-radius: 6px;
  width: 90%;
  max-width: 340px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: dialog-scale 0.2s ease-out;
}

@keyframes dialog-scale {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.alert-dialog__header {
  padding: 24px 24px 0;
}

.alert-dialog__title {
  font-family: 'Inter';
  font-size: 18px;
  font-weight: 600;
  color: #1c1c1c;
  margin-bottom: 0;
  text-align: start;
  line-height: 1.4;
  letter-spacing: -0.5px;
}

.alert-dialog__content {
  padding: 24px;
}

.alert-dialog__message {
  height: 60px;
  font-family: 'Inter';
  font-size: 16px;
  font-weight: 400;
  color: #6B6B6B;
  text-align: start;
  margin-top: 0;
  white-space: pre-wrap;
}

.alert-dialog__title.no-message {
  text-align: center;
  padding-top: 50px;
  padding-bottom: 20px;
}

.alert-dialog__actions {
  display: flex;
  padding: 0 24px 24px;
  gap: 12px;
}

.alert-dialog__button {
  flex: 1;
  height: 52px;
  font-family: 'Inter';
  font-size: 16px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: #0097A9;
}

.alert-dialog__button:active {
  background-color: #e9ecef;
}

.alert-dialog__button--cancel {
  background-color: white;
  color: #0097A9;
  border: 1px solid #0097A9;
}

.alert-dialog__button--confirm {
  color: white;
}

.alert-dialog__button--neutral {
  color: white;
  background-color: #98D3DD;
}

/* Only one button - takes full width */
.alert-dialog__actions:has(.alert-dialog__button:only-child) .alert-dialog__button {
  width: 100%;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
